import type { MirroredAppointment } from '../../db/schema';
import type { Product, RemainderKind, Service } from '../../wire';

/**
 * **The till's working shapes** — what a sale is while it is being rung up.
 *
 * Split out of `Checkout.tsx` when the screen became a cart (2026-09-30). None
 * of this is new: the line and split drafts, the appointment's lines, the
 * mapping to `actions.checkout`'s parts and the tender fallback are the ones the
 * register has always used, moved so the catalog, the cart and the payment
 * panel can each be a file of their own without importing the sheet.
 */

/** What the sale came to, kept for the panel that replaces the form. */
export interface CompletedSale {
  totalCents: number;
  /** What was actually handed over: the sale plus the tip (0046). */
  collectedCents: number;
  /**
   * **Every part of the payment.** A split sale has more than one, and a panel
   * that named only the first would tell the owner something incomplete about
   * the money she has just taken.
   */
  tenders: readonly { kind: RemainderKind; cents: number }[];
  clientId: string | null;
  clientName: string | null;
}

/**
 * One row of a split payment (0046).
 *
 * `text` empty on the **last** row means *the rest*, which is exactly what the
 * server reads an omitted amount as. An empty row anywhere else is the mistake
 * the preview refuses in the server's own words.
 */
export interface TenderDraft {
  key: string;
  kind: RemainderKind;
  text: string;
  label: string;
}

let tenderSeq = 0;
export const nextTenderKey = (): string => `tender-${++tenderSeq}`;

export interface LineDraft {
  /** Local key. Two jars of the same oil are two rows if the owner wants them. */
  key: string;
  itemId: string;
  name: string;
  /**
   * **Which length, in words** — "60 min", or the option's own name when it
   * does not change the span. Display only; the id below is what the server
   * reads.
   */
  lengthLabel?: string | undefined;
  qty: number;
  /**
   * Blank means "the catalog's price". The cart never offers a box to type over
   * a priced item's list price (a discount is how she gives something away);
   * the only things that fill it are an **open-price** line's typed amount and
   * a pickup order's **quoted** price (C1 → B4).
   */
  priceText: string;
  /**
   * `items.open_price` — for this line the price is not an override, it is
   * **the** price, and blank is a refusal rather than "the catalog's" (B4).
   */
  openPrice: boolean;
  /** "coral scarf" — what a generic line actually was. Optional (B4). */
  note: string;
  /**
   * The list price to use when the catalog does not have this item mirrored —
   * or, for an optioned or attached line, the **option's / the link's** price.
   * The server prices lines from `items` and the ids below, so the preview has
   * to use the same number or the two would disagree about the subtotal.
   */
  fallbackListCents?: number | undefined;
  /**
   * **Which way this service was sold** (migration 0048). The list price is the
   * option's, not the item's headline, and the request carries the id so the
   * server prices the list from the option too (§7.4 rule 3).
   */
  optionId?: string | undefined;
  /**
   * **The parent this line was attached to** (0048) — a service id. An add-on
   * costs less attached than standing alone, and naming the parent is what lets
   * the server look that number up.
   */
  addOnOf?: string | undefined;
}

let lineSeq = 0;
export const nextKey = (): string => `line-${++lineSeq}`;

/** Three rows of digits, then zero, a point and a backspace — a calculator. */
export const KEYPAD_KEYS = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', '⌫'] as const;

/** Today's three, for a `core-api` that has not shipped `till` yet. */
const FALLBACK_TENDERS: readonly RemainderKind[] = ['cash', 'card', 'other'];

/**
 * **The buttons this shop's till draws** — `session.till.tenders`, or today's
 * three when the session predates the field.
 *
 * Shared with the gift card sale (`Certificates.tsx`, migration 0070): a second
 * list of "how this shop is paid" would be the one that forgets the owner
 * dropped cards.
 */
export function tillTendersFor(
  session: { till?: { tenders?: readonly RemainderKind[] | undefined } | undefined } | null,
): readonly RemainderKind[] {
  return session?.till?.tenders ?? FALLBACK_TENDERS;
}

/**
 * **The visit, as till lines** (migration 0048) — the service and then one line
 * per add-on, the shape the booking wrote into `appointment_services`.
 *
 * `appointment.price_cents` is the **whole** booking — the option's price plus
 * every attached price — so the service line's own list is that total *minus*
 * the attachments, or a $300 facial would ring up at $345. The appointment
 * carries the numbers the guest was **quoted**, and a price the owner has since
 * edited must not rewrite what somebody was promised.
 */
export function linesFromAppointment(appointment: MirroredAppointment): LineDraft[] {
  const addOns = appointment.add_ons ?? [];
  const attachedCents = addOns.reduce((total, a) => total + a.price_cents, 0);
  return [
    {
      key: nextKey(),
      itemId: appointment.service_id,
      name: appointment.service_name,
      ...(appointment.option_name === null || appointment.option_name === undefined
        ? {}
        : { lengthLabel: appointment.option_name }),
      qty: 1,
      priceText: '',
      openPrice: false,
      note: '',
      fallbackListCents: Math.max(0, appointment.price_cents - attachedCents),
      ...(appointment.option_id === null || appointment.option_id === undefined
        ? {}
        : { optionId: appointment.option_id }),
    },
    ...addOns.flatMap<LineDraft>((a) =>
      // A line with no item id cannot be rung up — there is no catalog row for
      // the server to price or classify against, and §7.3.1 has no free-text
      // line. Nothing writes one today; this is the guard rather than a cast.
      a.service_id === null
        ? []
        : [
            {
              key: nextKey(),
              itemId: a.service_id,
              name: a.name,
              qty: 1,
              priceText: '',
              openPrice: false,
              note: '',
              fallbackListCents: a.price_cents,
              addOnOf: appointment.service_id,
            },
          ],
    ),
  ];
}

/**
 * One part of a split, as `actions.checkout` takes it (0046). The preview's own
 * list is what is mapped, so the total the owner watched and the amounts the
 * server is told come from the same reading of the boxes.
 */
export function toActionPart(part: {
  kind: RemainderKind;
  cents?: number | undefined;
  label?: string;
}): {
  kind: RemainderKind;
  amountCents?: number | undefined;
  label?: string | undefined;
} {
  return {
    kind: part.kind,
    ...(part.cents === undefined ? {} : { amountCents: part.cents }),
    ...(part.label === undefined ? {} : { label: part.label }),
  };
}

/* ------------------------------------------------------------------ */
/* The catalog, as the till shows it                                   */
/* ------------------------------------------------------------------ */

export type ServiceOption = Service['options'][number];

/**
 * The ways a service is sold today, in her order. Retired lengths never show.
 * `?? []` because a row mirrored before the field existed is not re-parsed.
 */
export function liveOptions(service: Service): ServiceOption[] {
  return (service.options ?? [])
    .filter((o) => o.active)
    .sort((a, b) => a.sort_order - b.sort_order || a.price_cents - b.price_cents);
}

/**
 * **Her headings, in her order** — `category` groups, and a heading's place is
 * its first row's place, because the list arrives sorted by `sort_order`
 * already (the mirror's order, `Settings.tsx`'s `groupByCategory`). Rows with
 * no heading form one unheaded group where its first row falls.
 */
export function groupServices(rows: readonly Service[]): [string | null, Service[]][] {
  const groups = new Map<string | null, Service[]>();
  for (const s of rows) {
    const heading = s.category ?? null;
    const list = groups.get(heading);
    if (list === undefined) groups.set(heading, [s]);
    else list.push(s);
  }
  return [...groups.entries()];
}

/** What can be rung up standing alone: active, and not attach-only (0048). */
export function sellableServices(services: readonly Service[]): Service[] {
  return services.filter((s) => s.active && s.attach_only !== true);
}

export function sellableProducts(products: readonly Product[]): Product[] {
  return products.filter((p) => p.active);
}

/**
 * **What can be attached to what is already in the cart** — the "add to <her
 * facial>" suggestions. Only parents present as a standalone line, only links
 * still on, and never a second copy of an add-on already attached to that
 * parent.
 */
export function addOnSuggestions(
  lines: readonly LineDraft[],
  services: readonly Service[],
): { parent: Service; addOns: Service['add_ons'] }[] {
  const out: { parent: Service; addOns: Service['add_ons'] }[] = [];
  const seen = new Set<string>();
  for (const line of lines) {
    if (line.addOnOf !== undefined || seen.has(line.itemId)) continue;
    const parent = services.find((s) => s.service_id === line.itemId);
    if (parent === undefined) continue;
    seen.add(parent.service_id);
    const attached = new Set(
      lines.filter((l) => l.addOnOf === parent.service_id).map((l) => l.itemId),
    );
    const addOns = (parent.add_ons ?? [])
      .filter((a) => a.active && !attached.has(a.service_id))
      .sort((a, b) => a.sort_order - b.sort_order);
    if (addOns.length > 0) out.push({ parent, addOns });
  }
  return out;
}
